import { useEffect, useRef, useState } from "react";

export default function Home() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const letterRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const serviceMenuRef = useRef<HTMLDivElement>(null);

  const leftWord = "STUDIO";
  const rightWord = "DIGITAL";

  // Capture video duration once metadata is loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => {
      setDuration(video.duration || 0);
      setVideoReady(true);
      // Make sure it's paused on first frame
      try {
        video.pause();
        video.currentTime = 0;
      } catch {
        /* noop */
      }
    };

    if (video.readyState >= 1 && video.duration) {
      handleLoaded();
    } else {
      video.addEventListener("loadedmetadata", handleLoaded);
    }
    return () => {
      video.removeEventListener("loadedmetadata", handleLoaded);
    };
  }, []);

  // Map scroll progress within the hero wrapper to video.currentTime
  useEffect(() => {
    if (!videoReady || duration <= 0) return;

    let rafId = 0;
    let targetTime = 0;
    let currentTime = 0;

    const update = () => {
      const section = sectionRef.current;
      const video = videoRef.current;
      if (!section || !video) return;

      const rect = section.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // total scrollable distance is sectionHeight - viewportH
      const total = section.offsetHeight - viewportH;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const progress = total > 0 ? scrolled / total : 0;

      targetTime = progress * duration;

      const tick = () => {
        // Smoothly interpolate towards target time for a buttery frame-by-frame feel
        const diff = targetTime - currentTime;
        if (Math.abs(diff) < 0.005) {
          currentTime = targetTime;
        } else {
          currentTime += diff * 0.15;
        }
        if (video.readyState >= 2) {
          try {
            video.currentTime = currentTime;
          } catch {
            /* ignore seek errors */
          }
        }
        if (Math.abs(targetTime - currentTime) > 0.005) {
          rafId = requestAnimationFrame(tick);
        }
      };

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(tick);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      cancelAnimationFrame(rafId);
    };
  }, [videoReady, duration]);

  // Scroll-driven service menu: slide from right to center while fading out
  useEffect(() => {
    const animateServiceMenu = () => {
      const section = sectionRef.current;
      const menu = serviceMenuRef.current;
      if (!section || !menu) return;
      const rect = section.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = section.offsetHeight - viewportH;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const progress = total > 0 ? scrolled / total : 0;

      // Animate within the first 60% of scroll progress
      const local = Math.min(Math.max(progress / 0.6, 0), 1);
      // easeInOutCubic
      const eased =
        local < 0.5
          ? 4 * local * local * local
          : 1 - Math.pow(-2 * local + 2, 3) / 2;

      // Menu stays anchored to the right edge; slides down to vertical center on the right.
      const x = 0;
      const y = (viewportH / 2 - menu.offsetHeight / 2 - 40) * eased;
      // Fade out a bit in the middle of the journey, then come back so it ends fully visible at right-center
      // Use a sine curve dipping to ~30% opacity around the midpoint
      const dip = Math.sin(local * Math.PI);
      const opacity = 1 - dip * 0.7;

      menu.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      menu.style.opacity = String(opacity);
    };

    animateServiceMenu();
    window.addEventListener("scroll", animateServiceMenu, { passive: true });
    window.addEventListener("resize", animateServiceMenu);
    return () => {
      window.removeEventListener("scroll", animateServiceMenu);
      window.removeEventListener("resize", animateServiceMenu);
    };
  }, []);

  // Scroll-driven per-letter cascading drop animation
  useEffect(() => {
    const animateLetters = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = section.offsetHeight - viewportH;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const progress = total > 0 ? scrolled / total : 0;

      const letters = letterRefs.current;
      const count = letters.length;
      // Spread letter animations across the first 80% of scroll progress
      const span = 0.8;
      const perLetter = span / count;
      // Each letter's own animation duration (overlap a bit for smoother cascade)
      const dur = perLetter * 2.2;

      letters.forEach((el, i) => {
        if (!el) return;
        const startAt = i * perLetter;
        const local = Math.min(Math.max((progress - startAt) / dur, 0), 1);
        // easeOutCubic: gentle settle
        const eased = 1 - Math.pow(1 - local, 3);
        // Letters start 400px below their natural position and rise up to 0
        const startY = 400;
        const y = startY * (1 - eased);
        el.style.transform = `translate3d(0, ${y}px, 0)`;
      });
    };

    animateLetters();
    window.addEventListener("scroll", animateLetters, { passive: true });
    window.addEventListener("resize", animateLetters);
    return () => {
      window.removeEventListener("scroll", animateLetters);
      window.removeEventListener("resize", animateLetters);
    };
  }, []);

  return (
    <div
      ref={sectionRef}
      className="relative w-full bg-[#e9eaec]"
      style={{ height: "300vh", fontFamily: "'Inter', sans-serif" }}
    >
      {/* Top navigation bar — ultra subtle, blends with background */}
      <header className="fixed top-0 left-0 right-0 z-50 px-10 pt-5">
        <div className="w-full flex items-center justify-between text-black/70">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-2 cursor-pointer group"
          >
            <span className="w-2 h-2 flex items-center justify-center">
              <span className="w-1.5 h-1.5 rounded-full bg-black/70 group-hover:bg-black transition"></span>
            </span>
            <span
              className="text-[12px] tracking-[0.3em] uppercase font-medium group-hover:text-black transition"
              style={{ fontFamily: "'Antonio', sans-serif" }}
            >
              Hubit
            </span>
          </a>

          {/* Center nav links */}
          <nav className="hidden md:flex items-center gap-8 text-[10px] tracking-[0.3em] uppercase">
            <a href="#index" className="hover:text-black transition cursor-pointer whitespace-nowrap">Index</a>
            <a href="#studio" className="hover:text-black transition cursor-pointer whitespace-nowrap">Studio</a>
            <a href="#archive" className="hover:text-black transition cursor-pointer whitespace-nowrap">Archive</a>
            <a href="#journal" className="hover:text-black transition cursor-pointer whitespace-nowrap">Journal</a>
          </nav>

          {/* Right meta */}
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase">
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-black/70 inline-block animate-pulse"></span>
              <span>Available 2026</span>
            </span>
            <a
              href="#contact"
              className="hover:text-black transition cursor-pointer whitespace-nowrap"
            >
              Menu&nbsp;<i className="ri-add-line text-[12px]"></i>
            </a>
          </div>
        </div>
      </header>

      {/* Sticky hero stage: stays in view while we scroll through the wrapper */}
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Background video */}
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            src="https://public.readdy.ai/ai/video_res/019ed4ab-00b1-7361-9888-35a790a6da77.mp4"
            preload="auto"
            muted
            playsInline
            // @ts-expect-error iOS Safari hint for inline frame-accurate playback
            disableRemotePlayback
            className="w-full h-full object-cover"
            style={{
              imageRendering: "auto",
              willChange: "transform",
              transform: "translateZ(0)",
            }}
          />

        </div>

        {/* Hero content layer */}
        <section className="relative z-10 h-screen w-full flex flex-col px-10 py-8">
          {/* Top row: big split title + center tag */}
          <div className="relative w-full flex items-start justify-between">
            {/* Left big word */}
            <h1
              className="text-black leading-none tracking-tight select-none flex"
              style={{
                fontFamily: "'Antonio', sans-serif",
                fontSize: "clamp(80px, 13vw, 220px)",
                fontWeight: 500,
                letterSpacing: "-0.02em",
              }}
            >
              {leftWord.split("").map((ch, i) => (
                <span
                  key={`l-${i}`}
                  ref={(el) => {
                    letterRefs.current[i] = el;
                  }}
                  className="inline-block"
                  style={{
                    willChange: "transform",
                    transition:
                      "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                    transformOrigin: "50% 0%",
                    transform: "translate3d(0, 400px, 0)",
                  }}
                >
                  {ch}
                </span>
              ))}
            </h1>

            {/* Center small tag */}
            <div className="absolute left-1/2 top-6 -translate-x-1/2 flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-black/80">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-asterisk text-[14px]"></i>
              </span>
              <span>{`{ Brand Direction }`}</span>
            </div>

            {/* Right big word */}
            <h1
              className="text-black leading-none tracking-tight select-none flex"
              style={{
                fontFamily: "'Antonio', sans-serif",
                fontSize: "clamp(80px, 13vw, 220px)",
                fontWeight: 500,
                letterSpacing: "-0.02em",
              }}
            >
              {rightWord.split("").map((ch, i) => (
                <span
                  key={`r-${i}`}
                  ref={(el) => {
                    letterRefs.current[leftWord.length + i] = el;
                  }}
                  className="inline-block"
                  style={{
                    willChange: "transform",
                    transition:
                      "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
                    transformOrigin: "50% 0%",
                    transform: "translate3d(0, 400px, 0)",
                  }}
                >
                  {ch}
                </span>
              ))}
            </h1>
          </div>

          {/* Middle row: side meta info */}
          <div className="relative flex-1 w-full flex -translate-y-[110px]">
            {/* Right service menu */}
            <div
              ref={serviceMenuRef}
              className="absolute right-0 top-2 text-right text-[12px] leading-relaxed text-black/85 tracking-wide space-y-1"
              style={{
                willChange: "transform, opacity",
                transition:
                  "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease-out",
              }}
            >
              <p className="hover:text-black cursor-pointer transition">Creative Strategy</p>
              <p className="hover:text-black cursor-pointer transition">Brand Identity</p>
              <p className="hover:text-black cursor-pointer transition">Creative Direction</p>
              <p className="hover:text-black cursor-pointer transition">Web Design</p>
            </div>

            {/* Lower-left nav */}
            <div className="absolute left-0 bottom-32 flex flex-col gap-4">
              <nav className="flex items-center gap-6 text-[12px] tracking-[0.2em] uppercase text-black">
                <a href="#about" className="hover:opacity-60 transition whitespace-nowrap cursor-pointer">About</a>
                <a href="#work" className="hover:opacity-60 transition whitespace-nowrap cursor-pointer">Work</a>
                <a href="#labs" className="hover:opacity-60 transition whitespace-nowrap cursor-pointer">Labs</a>
                <a href="#contact" className="hover:opacity-60 transition whitespace-nowrap cursor-pointer">Contact</a>
              </nav>
            </div>
          </div>

          {/* Bottom row: contact, description, socials */}
          <div className="relative w-full grid grid-cols-3 items-end text-[11px] text-black/85 leading-relaxed">
            {/* Left description */}
            <div className="max-w-[220px]">
              <p>
                Strategy, Design and Development. Lightning-fast, lean and sensibly priced.
              </p>
            </div>

            {/* Center contact */}
            <div className="text-center">
              <p>hub@hubitdigital.co</p>
              <p>0982&middot;780 522</p>
              <p>Hubit Creative Studio, 2026&copy;</p>
            </div>

            {/* Right socials */}
            <div className="text-right space-y-0.5">
              <p className="hover:text-black cursor-pointer transition">Instagram</p>
              <p className="hover:text-black cursor-pointer transition">Threads</p>
              <p className="hover:text-black cursor-pointer transition">Facebook</p>
            </div>

            {/* Scroll hint */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-10 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-black/70 animate-pulse">
              <i className="ri-arrow-down-line text-[14px] w-3 h-3 flex items-center justify-center"></i>
              <span>Scroll to play</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}